"use client";

import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useId, useRef, useState } from "react";
import Script from "next/script";

const DEFAULT_APOLLO_APP_ID = "69cba950a2537700193a6065";
const APOLLO_WIDGET_SCRIPT_SRC = "https://assets.apollo.io/js/meetings/meetings-widget.js";
const APOLLO_WIDGET_SCRIPT_ID = "apollo-meetings-widget-script";
const APOLLO_FORM_ID_QUERY_PARAM = "form_id";
const APOLLO_SUBMISSION_QUEUE_MAX = 100;
const APOLLO_FORM_DEBUG_ENABLED = process.env.NODE_ENV !== "production";
const APOLLO_WIDGET_DOM_SELECTOR =
	"#apollo-meetings-widget, [id^='apollo-meetings-widget'], [id*='apollo-meetings-widget'], [class*='apollo-meetings-widget']";

interface ApolloSubmitOptions {
	formId?: string;
	onError?: (error: unknown) => void;
	onSuccess?: () => void;
	onRouted?: () => void;
}

interface ApolloMatchedActionPayload {
	matched_action?: unknown;
	matchedAction?: unknown;
	routing_form_response_id?: string;
	routingFormResponseId?: string;
	success?: boolean;
	data?: unknown;
	result?: unknown;
	[key: string]: unknown;
}

type ApolloPayloadListener = (payload: ApolloMatchedActionPayload) => void;

const is_apollo_meetings_request_url = (url: string): boolean => {
	const normalized = url.toLowerCase();
	return (
		(normalized.includes("app.apollo.io/api/v1") &&
			(normalized.includes("meetings") ||
				normalized.includes("inbound_router") ||
				normalized.includes("routing_form") ||
				normalized.includes("guest_inbound_router"))) ||
		normalized.includes("apollo.io/api/v1/meetings") ||
		normalized.includes("apollo.io/api/v1/meetings/inbound_router")
	);
};

const is_apollo_submission_request_url = (url: string): boolean => {
	const normalized = url.toLowerCase();
	return (
		normalized.includes("inbound_router") ||
		normalized.includes("routing_form") ||
		normalized.includes("guest_inbound_router")
	);
};

const is_object_record = (value: unknown): value is Record<string, unknown> => {
	return typeof value === "object" && value !== null && !Array.isArray(value);
};

const has_matched_action = (value: unknown): value is ApolloMatchedActionPayload => {
	return is_object_record(value) && ("matched_action" in value || "matchedAction" in value);
};

const has_submission_result_details = (value: unknown): value is ApolloMatchedActionPayload => {
	if (!is_object_record(value)) return false;
	return (
		"routing_form_response_id" in value ||
		"routingFormResponseId" in value ||
		"mandatory_questions" in value ||
		"error_code" in value ||
		has_matched_action(value)
	);
};

const with_form_id_query_param = (url: string, form_id: string): string => {
	try {
		const normalized_url = new URL(url, window.location.origin);
		normalized_url.searchParams.set(APOLLO_FORM_ID_QUERY_PARAM, form_id);
		return normalized_url.toString();
	} catch {
		return url;
	}
};

declare global {
	interface Window {
		ApolloMeetings?: {
			initWidget: (config: Record<string, unknown>) => unknown;
			submit: (options?: ApolloSubmitOptions) => unknown;
		};
	}
}

let apollo_shims_ref_count = 0;
let apollo_original_fetch: typeof fetch | null = null;
let apollo_original_xhr_open: typeof XMLHttpRequest.prototype.open | null = null;
let apollo_original_xhr_send: typeof XMLHttpRequest.prototype.send | null = null;
const apollo_submission_form_id_queue: string[] = [];
const apollo_request_form_id_map = new WeakMap<Request, string>();
const apollo_xhr_request_url_map = new WeakMap<XMLHttpRequest, string>();
const apollo_xhr_form_id_map = new WeakMap<XMLHttpRequest, string>();
const apollo_payload_listeners = new Map<string, Set<ApolloPayloadListener>>();
let apollo_last_global_payload: ApolloMatchedActionPayload | null = null;
let apollo_last_global_payload_timestamp = 0;
let apollo_form_url_owner_token: symbol | null = null;

const get_request_url_from_input = (request_input: Request | URL | string): string => {
	return typeof request_input === "string"
		? request_input
		: request_input instanceof URL
			? request_input.toString()
			: request_input.url ?? "";
};

const get_form_id_from_request_url = (url: string): string | null => {
	try {
		const normalized_url = new URL(url, window.location.origin);
		const form_id = normalized_url.searchParams.get(APOLLO_FORM_ID_QUERY_PARAM);
		return form_id && form_id.length > 0 ? form_id : null;
	} catch {
		return null;
	}
};

const queue_form_id_for_next_submission = (form_id: string) => {
	apollo_submission_form_id_queue.push(form_id);
	if (apollo_submission_form_id_queue.length > APOLLO_SUBMISSION_QUEUE_MAX) {
		apollo_submission_form_id_queue.splice(0, apollo_submission_form_id_queue.length - APOLLO_SUBMISSION_QUEUE_MAX);
	}
};

const consume_next_submission_form_id = (): string | null => {
	const form_id = apollo_submission_form_id_queue.shift();
	return form_id ?? null;
};

const resolve_form_id_for_apollo_request = (request_url: string): string | null => {
	const existing_form_id = get_form_id_from_request_url(request_url);
	if (existing_form_id) return existing_form_id;
	if (!is_apollo_submission_request_url(request_url)) return null;
	return consume_next_submission_form_id();
};

const dispatch_apollo_payload = (form_id: string | null, payload: ApolloMatchedActionPayload) => {
	if (APOLLO_FORM_DEBUG_ENABLED) {
		console.log("[ApolloForm][intercepted-payload]", {
			form_id,
			payload,
		});
	}
	apollo_last_global_payload = payload;
	apollo_last_global_payload_timestamp = Date.now();
	if (!form_id) return;
	const listeners = apollo_payload_listeners.get(form_id);
	if (!listeners || listeners.size === 0) return;
	listeners.forEach((listener) => {
		listener(payload);
	});
};

const uninstall_apollo_request_shims = () => {
	if (typeof window === "undefined") return;
	apollo_shims_ref_count = Math.max(0, apollo_shims_ref_count - 1);
	if (apollo_shims_ref_count > 0) return;

	if (apollo_original_fetch) {
		window.fetch = apollo_original_fetch;
	}
	if (apollo_original_xhr_open) {
		XMLHttpRequest.prototype.open = apollo_original_xhr_open;
	}
	if (apollo_original_xhr_send) {
		XMLHttpRequest.prototype.send = apollo_original_xhr_send;
	}
	apollo_original_fetch = null;
	apollo_original_xhr_open = null;
	apollo_original_xhr_send = null;
	apollo_submission_form_id_queue.length = 0;
};

const install_apollo_request_shims = (): (() => void) => {
	if (typeof window === "undefined") {
		return () => {};
	}

	apollo_shims_ref_count += 1;
	if (apollo_shims_ref_count > 1) {
		return uninstall_apollo_request_shims;
	}

	apollo_original_fetch = window.fetch.bind(window);
	apollo_original_xhr_open = XMLHttpRequest.prototype.open;
	apollo_original_xhr_send = XMLHttpRequest.prototype.send;

	window.fetch = (async (...args: Parameters<typeof fetch>) => {
		let patched_args: Parameters<typeof fetch> = args;
		let request_form_id: string | null = null;
		try {
			const request_input = args[0];
			const request_url = get_request_url_from_input(request_input);

			if (is_apollo_meetings_request_url(request_url)) {
				request_form_id = resolve_form_id_for_apollo_request(request_url);
				const patched_url =
					request_form_id && !get_form_id_from_request_url(request_url)
						? with_form_id_query_param(request_url, request_form_id)
						: request_url;

				if (request_input instanceof Request) {
					const patched_request = new Request(patched_url, request_input);
					if (request_form_id) {
						apollo_request_form_id_map.set(patched_request, request_form_id);
					}
					patched_args = [patched_request, ...args.slice(1)] as Parameters<typeof fetch>;
				} else if (request_input instanceof URL) {
					patched_args = [new URL(patched_url), ...args.slice(1)] as Parameters<typeof fetch>;
				} else {
					patched_args = [patched_url, ...args.slice(1)] as Parameters<typeof fetch>;
				}
			}
		} catch {
			// Ignore URL patching errors and preserve normal behavior.
		}

		const response = await apollo_original_fetch!(...patched_args);

		try {
			const request_input = patched_args[0];
			const request_url = get_request_url_from_input(request_input);
			const is_apollo_meetings_request = is_apollo_meetings_request_url(request_url);

			if (!is_apollo_meetings_request) return response;
			if (!response.ok) return response;

			const cloned_response = response.clone();
			const response_text = await cloned_response.text();
			if (!response_text) return response;

			const json_payload = JSON.parse(response_text) as ApolloMatchedActionPayload;
			if (has_submission_result_details(json_payload)) {
				const resolved_form_id =
					get_form_id_from_request_url(request_url) ||
					(request_input instanceof Request ? apollo_request_form_id_map.get(request_input) ?? null : request_form_id);
				dispatch_apollo_payload(resolved_form_id, json_payload);
			}
		} catch {
			// Ignore payload sniffing errors and preserve normal behavior.
		}

		return response;
	}) as typeof fetch;

	XMLHttpRequest.prototype.open = function (
		method: string,
		url: string | URL,
		async?: boolean,
		username?: string | null,
		password?: string | null
	): void {
		const request_url = typeof url === "string" ? url : url.toString();
		let patched_url = request_url;

		if (is_apollo_meetings_request_url(request_url)) {
			const form_id_for_request = resolve_form_id_for_apollo_request(request_url);
			if (form_id_for_request) {
				apollo_xhr_form_id_map.set(this, form_id_for_request);
				if (!get_form_id_from_request_url(request_url)) {
					patched_url = with_form_id_query_param(request_url, form_id_for_request);
				}
			}
		}

		apollo_xhr_request_url_map.set(this, patched_url);
		apollo_original_xhr_open!.call(this, method, patched_url, async ?? true, username ?? null, password ?? null);
	};

	XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null): void {
		this.addEventListener("load", () => {
			try {
				const request_url = apollo_xhr_request_url_map.get(this) ?? "";
				if (!is_apollo_meetings_request_url(request_url)) return;
				if (this.status < 200 || this.status >= 300) return;

				const response_text = this.responseText;
				if (!response_text) return;
				const json_payload = JSON.parse(response_text) as ApolloMatchedActionPayload;
				if (has_submission_result_details(json_payload)) {
					const resolved_form_id = apollo_xhr_form_id_map.get(this) ?? get_form_id_from_request_url(request_url);
					dispatch_apollo_payload(resolved_form_id, json_payload);
				}
			} catch {
				// Ignore payload sniffing errors and preserve normal behavior.
			}
		});

		apollo_original_xhr_send!.call(this, body);
	};

	return uninstall_apollo_request_shims;
};

const subscribe_to_apollo_payloads = (form_id: string, listener: ApolloPayloadListener): (() => void) => {
	const existing_listeners = apollo_payload_listeners.get(form_id);
	if (existing_listeners) {
		existing_listeners.add(listener);
	} else {
		apollo_payload_listeners.set(form_id, new Set([listener]));
	}

	return () => {
		const listeners = apollo_payload_listeners.get(form_id);
		if (!listeners) return;
		listeners.delete(listener);
		if (listeners.size === 0) {
			apollo_payload_listeners.delete(form_id);
		}
	};
};

const get_recent_global_payload = (max_age_ms = 8000): ApolloMatchedActionPayload | null => {
	if (!apollo_last_global_payload) return null;
	if (Date.now() - apollo_last_global_payload_timestamp > max_age_ms) return null;
	return apollo_last_global_payload;
};

const remove_hidden_apollo_widget_nodes = () => {
	if (typeof document === "undefined") return;
	const widget_nodes = Array.from(document.querySelectorAll<HTMLElement>(APOLLO_WIDGET_DOM_SELECTOR)).filter(
		(node) => node.tagName.toLowerCase() !== "script"
	);
	for (const widget_node of widget_nodes) {
		const aria_hidden = widget_node.getAttribute("aria-hidden") === "true";
		const style = window.getComputedStyle(widget_node);
		const is_hidden_by_style = style.display === "none" || style.visibility === "hidden" || style.opacity === "0";
		const is_non_interactive = style.pointerEvents === "none";
		if (aria_hidden || is_hidden_by_style || is_non_interactive) {
			widget_node.remove();
		}
	}
};

interface ApolloFormProps {
	scheduling_link: string;
	app_id?: string;
	form_id?: string;
	widget_options?: Record<string, unknown>;
	className?: string;
	children?: ReactNode;
	show_submit_button?: boolean;
	submit_button_text?: string;
	on_submit_error?: (error: unknown) => void;
	on_submit_success?: (payload?: unknown) => void;
}

type FormSubmitHandler = NonNullable<ComponentPropsWithoutRef<"form">["onSubmit"]>;

interface ApolloFormProviderState {
	is_ready: boolean;
	is_loading: boolean;
}

const ApolloFormProviderStateContext = createContext<ApolloFormProviderState | null>(null);

export const use_apollo_form_provider_state = (): ApolloFormProviderState | null => {
	return useContext(ApolloFormProviderStateContext);
};

export function ApolloFormProvider({
	scheduling_link,
	app_id = DEFAULT_APOLLO_APP_ID,
	form_id,
	widget_options,
	className = "w-full",
	children,
	show_submit_button = true,
	submit_button_text = "Submit",
	on_submit_error,
	on_submit_success,
}: ApolloFormProps) {
	const is_submitting_ref = useRef(false);
	const [script_loaded, set_script_loaded] = useState(false);
	const [widget_failed, set_widget_failed] = useState(false);
	const last_init_key_ref = useRef<string | null>(null);
	const last_apollo_payload_ref = useRef<ApolloMatchedActionPayload | null>(null);
	const last_emitted_response_id_ref = useRef<string | null>(null);
	const has_emitted_success_ref = useRef(false);
	const url_owner_token_ref = useRef(Symbol("apollo-form-url-owner"));
	const generated_form_id = useId();
	const widget_options_key = useMemo(() => {
		try {
			return JSON.stringify(widget_options ?? {});
		} catch {
			return "";
		}
	}, [widget_options]);
	const resolved_form_id = useMemo(() => {
		if (form_id) return form_id;
		return `apollo-form-${generated_form_id.replace(/[^a-zA-Z0-9_-]/g, "")}`;
	}, [form_id, generated_form_id]);
	const resolved_init_key = useMemo(() => {
		return `${app_id}:${scheduling_link}:${resolved_form_id}:${widget_options_key}`;
	}, [app_id, scheduling_link, resolved_form_id, widget_options_key]);
	const has_apollo_meetings = typeof window !== "undefined" && Boolean(window.ApolloMeetings);
	const is_ready = script_loaded && !widget_failed && has_apollo_meetings;
	const provider_state = useMemo<ApolloFormProviderState>(() => {
		return {
			is_ready,
			is_loading: !is_ready && !widget_failed,
		};
	}, [is_ready, widget_failed]);

	useEffect(() => {
		if (window.ApolloMeetings) {
			set_script_loaded(true);
		}
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const owner_token = url_owner_token_ref.current;
		if (apollo_form_url_owner_token !== null && apollo_form_url_owner_token !== owner_token) return;
		apollo_form_url_owner_token = owner_token;

		try {
			const current_url = new URL(window.location.href);
			if (current_url.searchParams.get(APOLLO_FORM_ID_QUERY_PARAM) !== resolved_form_id) {
				current_url.searchParams.set(APOLLO_FORM_ID_QUERY_PARAM, resolved_form_id);
				window.history.replaceState(window.history.state, "", current_url.toString());
			}
		} catch {
			// Ignore URL mutation errors and keep default behavior.
		}

		return () => {
			if (apollo_form_url_owner_token === owner_token) {
				apollo_form_url_owner_token = null;
			}
		};
	}, [resolved_form_id]);

	useEffect(() => {
		const uninstall_shims = install_apollo_request_shims();
		return uninstall_shims;
	}, []);

	useEffect(() => {
		return () => {
			remove_hidden_apollo_widget_nodes();
		};
	}, []);

	useEffect(() => {
		return subscribe_to_apollo_payloads(resolved_form_id, (payload) => {
			last_apollo_payload_ref.current = payload;
		});
	}, [resolved_form_id]);

	useEffect(() => {
		const is_sourcemap_noise = (value: string) => {
			const lower = value.toLowerCase();
			return lower.includes("sourcemap") || lower.includes(".map") || lower.includes("internal-assets.staging-gcp.apollo.io");
		};

		const handle_unhandled_rejection = (event: PromiseRejectionEvent) => {
			const reason_message = String(event.reason ?? "");
			if (is_sourcemap_noise(reason_message)) return;
			if (!reason_message.includes("apollo.io") && !reason_message.includes("ApolloMeetings")) return;
			event.preventDefault();
			set_widget_failed(true);
		};

		const handle_error = (event: ErrorEvent) => {
			const message = String(event.message ?? "");
			const filename = String(event.filename ?? "");
			if (is_sourcemap_noise(message) || is_sourcemap_noise(filename)) return;
			if (!message.includes("apollo.io") && !filename.includes("apollo.io")) return;
			event.preventDefault();
			set_widget_failed(true);
		};

		window.addEventListener("unhandledrejection", handle_unhandled_rejection);
		window.addEventListener("error", handle_error);

		return () => {
			window.removeEventListener("unhandledrejection", handle_unhandled_rejection);
			window.removeEventListener("error", handle_error);
		};
	}, []);

	useEffect(() => {
		if (!script_loaded || !window.ApolloMeetings) return;
		if (widget_failed) return;

		if (last_init_key_ref.current === resolved_init_key) return;
		last_init_key_ref.current = resolved_init_key;

		try {
			const maybe_promise = window.ApolloMeetings.initWidget({
				appId: app_id,
				schedulingLink: scheduling_link,
				formId: resolved_form_id,
				...widget_options,
			});

			if (maybe_promise && typeof (maybe_promise as Promise<unknown>).catch === "function") {
				(maybe_promise as Promise<unknown>).catch((error) => {
					console.error("[ApolloForm] initWidget failed:", error);
					set_widget_failed(true);
				});
			}
		} catch (error) {
			console.error("[ApolloForm] initWidget failed:", error);
			set_widget_failed(true);
		}
	}, [app_id, resolved_form_id, resolved_init_key, scheduling_link, script_loaded, widget_failed, widget_options]);

	const ensure_widget_initialized = async () => {
		if (!window.ApolloMeetings || widget_failed) return;
		if (last_init_key_ref.current === resolved_init_key) return;
		last_init_key_ref.current = resolved_init_key;

		const maybe_promise = window.ApolloMeetings.initWidget({
			appId: app_id,
			schedulingLink: scheduling_link,
			formId: resolved_form_id,
			...widget_options,
		});

		if (maybe_promise && typeof (maybe_promise as Promise<unknown>).then === "function") {
			await (maybe_promise as Promise<unknown>);
		}
	};

	const handle_submit: FormSubmitHandler = async (event) => {
		event.preventDefault();
		if (!window.ApolloMeetings || widget_failed) return;
		if (is_submitting_ref.current) return;
		is_submitting_ref.current = true;

		try {
			last_apollo_payload_ref.current = null;
			apollo_last_global_payload = null;
			apollo_last_global_payload_timestamp = 0;
			last_emitted_response_id_ref.current = null;
			has_emitted_success_ref.current = false;
			queue_form_id_for_next_submission(resolved_form_id);
			await ensure_widget_initialized();

			const wait_for_apollo_payload = async (timeout_ms = 5000, interval_ms = 50) => {
				const started_at = Date.now();
				while (Date.now() - started_at < timeout_ms) {
					if (last_apollo_payload_ref.current) return last_apollo_payload_ref.current;
					const recent_global_payload = get_recent_global_payload(timeout_ms + 1000);
					if (recent_global_payload) return recent_global_payload;
					await new Promise((resolve) => setTimeout(resolve, interval_ms));
				}
				return null;
			};

			const emit_success = async () => {
				let resolved_payload: ApolloMatchedActionPayload | Record<string, never> =
					(await wait_for_apollo_payload()) ?? {};
				if (APOLLO_FORM_DEBUG_ENABLED) {
					console.log("[ApolloForm][resolved-success-payload]", {
						form_id: resolved_form_id,
						resolved_payload,
					});
				}

				const response_id =
					typeof resolved_payload === "object" &&
					resolved_payload !== null &&
					("routing_form_response_id" in resolved_payload || "routingFormResponseId" in resolved_payload)
						? String(
								(resolved_payload as ApolloMatchedActionPayload).routing_form_response_id ??
								(resolved_payload as ApolloMatchedActionPayload).routingFormResponseId ??
								""
						  )
						: "";

				if (response_id) {
					if (last_emitted_response_id_ref.current === response_id) return;
					last_emitted_response_id_ref.current = response_id;
				} else {
					if (has_emitted_success_ref.current) return;
					has_emitted_success_ref.current = true;
				}

				on_submit_success?.(resolved_payload);
			};

			window.ApolloMeetings.submit({
				formId: resolved_form_id,
				onError: (error: unknown) => {
					console.error("[ApolloForm] submit failed:", error);
					on_submit_error?.(error);
				},
				onSuccess: () => {
					void emit_success();
				},
				onRouted: () => {
					void emit_success();
				},
			});
		} catch (error) {
			console.error("[ApolloForm] submit failed:", error);
			set_widget_failed(true);
			on_submit_error?.(error);
		} finally {
			is_submitting_ref.current = false;
		}
	};

	return (
		<ApolloFormProviderStateContext.Provider value={provider_state}>
			<div className={className}>
				<Script
					id={APOLLO_WIDGET_SCRIPT_ID}
					src={APOLLO_WIDGET_SCRIPT_SRC}
					strategy="afterInteractive"
					onLoad={() => set_script_loaded(true)}
					onError={() => set_widget_failed(true)}
				/>
				<form id={resolved_form_id} onSubmit={handle_submit}>
					{children}
					{show_submit_button && (
						<button type="submit" disabled={!is_ready}>
							{submit_button_text}
						</button>
					)}
				</form>
				{widget_failed && (
					<p className="mt-3 text-sm text-red-600">
						Unable to load the scheduling widget right now. Please refresh and try again.
					</p>
				)}
			</div>
		</ApolloFormProviderStateContext.Provider>
	);
}
